import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../store/useStore";
import WishlistButton from "./WishlistButton";
import usePincodeCheck from "../../utils/usePincodeCheck";
import { getProductPath } from "../../utils/productSlug";
import { FaShoppingCart, FaTimes } from "react-icons/fa";
import { getActivePincode } from "../../utils/location";

/* ─── Compact Notify Me (inline inside ProductCard) ─────────────────────────
 * A self-contained bell button that talks to the notifyMeSlice directly.
 * Kept intentionally compact to fit inside the 2-col action row.
 */
const CompactNotifyMe = ({ productId, variantId, pincode, isAuthenticated, openAuthModal }) => {
  const { notifyMeStatus, fetchNotifyMeStatus, registerNotifyMe, removeNotifyMe } = useStore();
  const key = `${productId}_${variantId}`;
  const status = notifyMeStatus[key] ?? 'idle';
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !productId || !variantId || hasFetched.current) return;
    hasFetched.current = true;
    fetchNotifyMeStatus({ product_id: productId, product_variant_id: variantId, pincode });
  }, [isAuthenticated, productId, variantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch on variant change
  useEffect(() => {
    if (!isAuthenticated || !productId || !variantId) return;
    hasFetched.current = false;
    fetchNotifyMeStatus({ product_id: productId, product_variant_id: variantId, pincode });
    hasFetched.current = true;
  }, [productId, variantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = status === 'loading';
  const isRegistered = status === 'registered';

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast('Login to get notified when back in stock', { icon: '🔔', id: 'auth-notify-card' });
      if (openAuthModal) openAuthModal();
      return;
    }
    if (isRegistered) {
      const res = await removeNotifyMe({ product_id: productId, product_variant_id: variantId });
      if (res.success) toast.success('Stock alert removed.', { id: 'notify-remove' });
      else toast.error(res.message || 'Failed to remove alert.', { id: 'notify-remove-err' });
    } else {
      const res = await registerNotifyMe({ product_id: productId, product_variant_id: variantId, pincode });
      if (res.success) toast.success("🔔 We'll notify you when it's back!", { id: 'notify-success', duration: 4000 });
      else toast.error(res.message || 'Could not register. Try again.', { id: 'notify-err' });
    }
  };

  return (
    <button
      id={`notify-me-card-${productId}-${variantId}`}
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      aria-label={isRegistered ? 'Remove stock alert' : 'Notify me when back in stock'}
      className={`col-span-2 flex w-full items-center justify-center gap-2 rounded-full py-2 text-xs font-bold shadow-sm transition-all duration-200 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 ${
        isRegistered
          ? 'border-2 border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100'
          : 'border-2 border-violet-500 bg-white text-violet-700 hover:bg-violet-50'
      }`}
    >
      {isLoading ? (
        <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
        </svg>
      ) : isRegistered ? (
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 9.172A4 4 0 0115 12v3m-6.586-6.586A4 4 0 008 12v3l-2 2h10M3 3l18 18M10 17a2 2 0 104 0" />
        </svg>
      ) : (
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.437L4 17h5m6 0a3 3 0 11-6 0" />
        </svg>
      )}
      <span>{isLoading ? 'Checking…' : isRegistered ? 'Remove Alert' : '🔔 Notify Me'}</span>
    </button>
  );
};

const getVariantKey = (variant) =>
  String(
    variant?.product_variant_id ?? variant?.variant_id ?? variant?.id ?? "",
  );

const getVariantLabel = (variant) =>
  [
    variant?.variant_name ?? variant?.size ?? variant?.name,
    variant?.unit_name ?? variant?.unit,
  ]
    .filter(Boolean)
    .join(" ");

const getVariantPrice = (variant, fallback) => {
  const value = Number.parseFloat(
    variant?.discount_price ?? variant?.price ?? fallback,
  );
  return Number.isFinite(value) ? value : fallback;
};

const getVariantOriginalPrice = (variant, fallback) => {
  const value = Number.parseFloat(
    variant?.original_price ?? variant?.originalPrice ?? fallback,
  );
  return Number.isFinite(value) ? value : fallback;
};

const getLocalizedNames = (product) =>
  [product.product_name_hindi, product.product_name_marathi]
    .filter((name) => name && name !== product.name)
    .filter((name, index, names) => names.indexOf(name) === index);


/**
 * ProductCard — reusable premium product card.
 * Props:
 *   product            — normalized product object from store
 *   compact            — boolean, smaller card for carousels
 *   showLocalizedNames — boolean, display Hindi/Marathi name subtitle
 */
const ProductCard = ({ product, compact = false, showLocalizedNames = true }) => {
  const addToCart = useStore((s) => s.addToCart);
  const incrementItem = useStore((s) => s.incrementItem);
  const decrementItem = useStore((s) => s.decrementItem);
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const openAuthModal = useStore((s) => s.openAuthModal);

  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showAllVariants, setShowAllVariants] = useState(false);
  const defaultPincode = useStore((s) => {
    return getActivePincode(s) || "";
  });
  const [pincode, setPincode] = useState(defaultPincode);
  const previousDefaultPincode = useRef(String(defaultPincode || "").trim());
  useEffect(() => {
    const nextDefault = String(defaultPincode || "").trim();
    if (nextDefault) {
      setPincode((current) =>
        !current || current === previousDefaultPincode.current ? nextDefault : current,
      );
    }
    previousDefaultPincode.current = nextDefault;
  }, [defaultPincode]);
  const productId = product?.id ?? product?.product_id;
  // Note: selectedVariant is declared below (useMemo), so we cannot reference it here.
  // We pass null for variantId; variant-level stock is read directly from selectedVariant.stock_status.
  const { status: pincodeStatus, message: pincodeMessage, productAvailability, stockLevel, availableStock } = usePincodeCheck(
    pincode,
    productId,
    null,
  );
  const variants = React.useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product?.variants]
  );

  // ── Initialise selectedVariantKey from the cart (if this product is already in cart) ──
  // We look at the cart synchronously during initialisation to pick the right variant key.
  const [selectedVariantKey, setSelectedVariantKey] = useState(() => {
    // We can't call useStore here, so fall back to variants[0] on first render.
    // The effect below will sync from cart on mount.
    return getVariantKey(variants[0] ?? null);
  });

  // Derive the selected variant object from the key
  const selectedVariant = React.useMemo(
    () => variants.find((v) => getVariantKey(v) === selectedVariantKey) || variants[0] || null,
    [variants, selectedVariantKey]
  );

  const visibleVariants = React.useMemo(() => {
    if (showAllVariants || variants.length <= 3) return variants;
    const selectedIdx = variants.findIndex(
      (v) => getVariantKey(v) === selectedVariantKey
    );
    if (selectedIdx >= 3) {
      return [variants[0], variants[1], variants[selectedIdx]];
    }
    return variants.slice(0, 3);
  }, [variants, showAllVariants, selectedVariantKey]);

  // Resolve selected variant's id for the cart selector below
  const selectedVariantId = React.useMemo(() => {
    const raw =
      selectedVariant?.product_variant_id ??
      selectedVariant?.variant_id ??
      selectedVariant?.id ??
      null;
    return raw != null ? String(raw) : null;
  }, [selectedVariant]);

  /**
   * Variant-scoped cart item lookup.
   * Only returns a cartItem when BOTH the product_id AND the selected variant match.
   * This prevents a 500g card from showing the quantity of a 1kg item in the cart.
   */
  const cartItem = useStore(
    useCallback(
      (s) => {
        if (!productId || !s.cart?.length) return null;

        // 1. Exact variant match
        const exactMatch = s.cart.find((item) => {
          if (String(item.product_id ?? item.id) !== String(productId)) return false;
          const itemVid =
            item.product_variant_id != null ? String(item.product_variant_id)
            : item.variant_id        != null ? String(item.variant_id)
            : null;
          if (!itemVid && !selectedVariantId) return true;
          if (!itemVid || !selectedVariantId) return false;
          return itemVid === selectedVariantId;
        });
        if (exactMatch) return exactMatch;

        // 2. Single-variant or no-variant cards (e.g. Related Products, Top Priority):
        // If the card has no variants or only 1 variant, any cart item matching product_id is this item
        if (variants.length <= 1 || !selectedVariantId) {
          const anyMatch = s.cart.find(
            (item) => String(item.product_id ?? item.id) === String(productId)
          );
          if (anyMatch) return anyMatch;
        }

        return null;
      },
      [productId, selectedVariantId, variants.length]
    )
  );

  /**
   * Build a map of variant key → cart item for all variants of this product.
   * Used to render per-variant in-cart badges on the pill buttons.
   */
  const variantCartMap = useStore(
    useCallback(
      (s) => {
        if (!productId || !s.cart?.length || variants.length === 0) return {};
        const map = {};
        for (const variant of variants) {
          const vKey = getVariantKey(variant);
          const vId =
            variant.product_variant_id != null ? String(variant.product_variant_id)
            : variant.variant_id        != null ? String(variant.variant_id)
            : variant.id               != null ? String(variant.id)
            : null;
          const found = s.cart.find((item) => {
            if (String(item.product_id ?? item.id) !== String(productId)) return false;
            const itemVid =
              item.product_variant_id != null ? String(item.product_variant_id)
              : item.variant_id        != null ? String(item.variant_id)
              : null;
            if (!itemVid && !vId) return true;
            if (!itemVid || !vId) return false;
            return itemVid === vId;
          });
          if (found) map[vKey] = found;
        }
        return map;
      },
      [productId, variants]
    )
  );

  const inWishlist = useStore(
    useCallback((s) => s.isInWishlist?.(productId), [productId])
  );

  /**
   * On mount: sync selectedVariantKey to whichever variant is already in the cart.
   * Only runs once (on mount) — does NOT override the user's manual pill selection.
   */
  useEffect(() => {
    if (!productId || variants.length === 0) return;
    // Read the store directly (not via hook) to avoid stale closure
    const currentCart = useStore.getState?.()?.cart ?? [];
    const inCartItem = currentCart.find(
      (item) => String(item.product_id ?? item.id) === String(productId)
    );
    if (!inCartItem) return;
    const cartVid =
      inCartItem.product_variant_id != null ? String(inCartItem.product_variant_id)
      : inCartItem.variant_id        != null ? String(inCartItem.variant_id)
      : null;
    if (!cartVid) return;
    const matched = variants.find(
      (v) => String(v.product_variant_id ?? v.variant_id ?? v.id ?? '') === cartVid
    );
    if (matched) {
      setSelectedVariantKey(getVariantKey(matched));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!product) return null;

  const basePrice = Number.parseFloat(product.price || 0) || 0;
  const baseOriginalPrice =
    Number.parseFloat(product.originalPrice || basePrice) || basePrice;
  const selectedPrice = getVariantPrice(selectedVariant, basePrice);
  const selectedOriginalPrice = getVariantOriginalPrice(
    selectedVariant,
    baseOriginalPrice,
  );
  const hasDiscount = selectedOriginalPrice > selectedPrice;
  const discountPercentage = hasDiscount
    ? Math.round(
        ((selectedOriginalPrice - selectedPrice) / selectedOriginalPrice) * 100,
      )
    : 0;
  const price = selectedPrice.toFixed(2);
  const originalPrice = selectedOriginalPrice.toFixed(2);
  const localizedNames = getLocalizedNames(product);

  const handleCardClick = () => {
    const productPath = getProductPath(product);
    if (productPath) navigate(productPath);
  };

  /* ── pincode helpers ── */
  const handlePincodeChange = (e) => {
    e.stopPropagation();
    const clean = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(clean);
  };

  const isPincodeComplete = pincode.length === 6;
  const isServiceable    = pincodeStatus === "serviceable";
  const isNotServiceable = pincodeStatus === "not_serviceable" || pincodeStatus === "error";
  const isCheckingPin    = pincodeStatus === "checking";
  const isPincodeOOS     = isServiceable && productAvailability?.product_available === false;

  // Global OOS: from API is_in_stock / stock_status / is_out_of_stock (no pincode needed)
  const isProductGloballyOOS =
    product.is_out_of_stock === true ||
    product.is_in_stock === false ||
    product.stock_status === "OUT_OF_STOCK" ||
    product.inStock === false ||
    (product.available_stock !== undefined && product.available_stock !== null && Number(product.available_stock) === 0);

  // Variant-level OOS: selected variant's own stock fields
  const isVariantOOS =
    selectedVariant?.is_out_of_stock === true ||
    selectedVariant?.is_in_stock === false ||
    selectedVariant?.stock_status === "OUT_OF_STOCK" ||
    (selectedVariant?.available_stock !== undefined && selectedVariant?.available_stock !== null && Number(selectedVariant?.available_stock) === 0);

  // Combined OOS: product is OOS if globally OOS, variant OOS, or pincode-based OOS
  const isOutOfStock = isProductGloballyOOS || isVariantOOS || isPincodeOOS;

  // ── Granular stock-level helpers (from location-aware API) ──────────────────
  // Use pincode-resolved stock first; fall back to product-level stock fields.
  const resolvedStockStatus = stockLevel ?? productAvailability?.stock_status ??
    selectedVariant?.stock_status ?? product.stock_status ?? null;
  const resolvedAvailableStock = availableStock ?? productAvailability?.available_stock ??
    selectedVariant?.available_stock ?? product.available_stock ?? null;
  const isLowStock     = !isOutOfStock && resolvedStockStatus === 'LOW_STOCK';     // 1-5 units
  const isLimitedStock = !isOutOfStock && resolvedStockStatus === 'LIMITED_STOCK'; // 6-10 units
  const isInStock      = !isOutOfStock && resolvedStockStatus === 'IN_STOCK';      // >10 units

  // For Notify Me: we need variant ID (fallback to first variant)
  const notifyVariantId =
    selectedVariant?.product_variant_id ??
    selectedVariant?.variant_id ??
    selectedVariant?.id ??
    variants[0]?.product_variant_id ??
    variants[0]?.variant_id ??
    null;

  const pinInputBorder = isPincodeComplete
    ? isServiceable && !isOutOfStock
      ? "border-[#00a859] text-[#00a859]"
      : isOutOfStock
        ? "border-amber-500 text-amber-600"
        : isNotServiceable
          ? "border-red-500 text-red-500"
          : "border-gray-300 text-gray-700"
    : "border-[#00a859] text-[#00a859]";

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (adding) return;
    setAdding(true);
    try {
      // Pass product with selectedVariant attached so cartSlice picks up the right variant id
      await addToCart(selectedVariant ? { ...product, selectedVariant } : product, 1);
      toast.success(`${product.name} added to cart!`, {
        icon: <FaShoppingCart />,
        id: `add-cart-${productId}`,
      });
    } finally {
      setAdding(false);
    }
  };

  const handleIncrement = async (e) => {
    e.stopPropagation();
    if (adding || !cartItem) return;
    // Pass the ACTUAL cart item — it carries the correct product_variant_id from the server
    await incrementItem(cartItem);
  };

  const handleDecrement = async (e) => {
    e.stopPropagation();
    if (adding || !cartItem) return;
    // Pass the ACTUAL cart item — it carries the correct product_variant_id from the server
    await decrementItem(cartItem);
  };

  /* Buy Now */
  const handleBuyNow = async (e) => {
    e.stopPropagation();
    if (adding) return;

    if (cartItem) {
      navigate("/cart");
      return;
    }

    setAdding(true);
    try {
      await addToCart(selectedVariant ? { ...product, selectedVariant } : product, 1);
      navigate("/cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-[box-shadow,transform,border-color] duration-300 ${
        isOutOfStock
          ? 'border-gray-200 opacity-90'
          : 'border-gray-200 hover:-translate-y-1 hover:border-[#00a859]/30 hover:shadow-xl'
      } ${compact ? 'min-w-[200px] max-w-[200px] sm:min-w-0 sm:max-w-none' : ''}`}
    >
      {/* ── Image area ── */}
      <div className="relative overflow-hidden bg-white">
        {/* Discount badge — orange pill top-left */}
        {hasDiscount && (
          <div className="absolute left-2 top-2 z-10 rounded-full bg-[#f97316] px-2.5 py-0.5 text-[10px] font-extrabold text-white shadow">
            {discountPercentage}% OFF
          </div>
        )}

        {/* Wishlist button — green circle top-right */}
        <div
          className={`absolute right-2.5 top-2.5 z-10 transition-opacity duration-200 ${
            inWishlist
              ? "opacity-100"
              : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
          }`}
        >
          <WishlistButton productId={productId} size="sm" />
        </div>

        {/* Product image */}
        <div className="relative aspect-square w-full overflow-hidden p-3 sm:p-4 flex items-center justify-center">
          {/* Out-of-stock overlay on image */}
          {isOutOfStock && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
              <span className="rounded-full bg-gray-800/80 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow">
                Out of Stock
              </span>
            </div>
          )}
          {product.image && !imgErr ? (
            <img
              src={product.image}
              alt={product.name}
              className="max-h-full max-w-full w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-105 m-auto drop-shadow-xs"
              onError={() => setImgErr(true)}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-gray-200">
              <svg className="h-12 w-12 sm:h-14 sm:w-14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-[11px] font-medium text-gray-300">No Image</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Info area ── */}
      <div className="flex flex-1 flex-col p-3 pt-1 sm:p-3.5 sm:pt-1.5">

        {/* Product name */}
        <h3 className="line-clamp-1 text-sm font-extrabold leading-snug text-gray-900 transition-colors group-hover:text-[#00a859] sm:text-[15px] mb-0.5">
          {product.name}
        </h3>
        {showLocalizedNames && localizedNames.length > 0 && (
          <p className="mb-1.5 line-clamp-1 text-[11px] leading-relaxed py-0.5 text-gray-500">
            {localizedNames.join(" / ")}
          </p>
        )}

        {/* Pricing: strikethrough + bold green */}
        <div className="mb-2 flex items-baseline gap-2">
          {hasDiscount && originalPrice !== price && (
            <span className="text-xs text-gray-400 line-through">₹ {originalPrice}</span>
          )}
          <span className="text-base font-extrabold text-[#00a859] sm:text-lg">
            ₹{price}
          </span>
        </div>

        {/* Variant pills */}
        {variants.length > 1 && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="mb-2.5 -mx-0.5 flex flex-nowrap overflow-x-auto gap-1 pt-3 pb-0.5 px-0.5 sm:flex-wrap sm:overflow-visible sm:pt-3"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {visibleVariants.map((variant) => {
              const variantKey = getVariantKey(variant);
              const isSelected  = getVariantKey(selectedVariant) === variantKey;
              const variantCartItem = variantCartMap[variantKey];
              const variantInCart   = !!variantCartItem;

              const vOrig = Number.parseFloat(variant?.original_price ?? variant?.originalPrice ?? 0);
              const vDisc = Number.parseFloat(variant?.discount_price ?? variant?.price ?? 0);
              const vDiscPct =
                vOrig > vDisc && vOrig > 0
                  ? Math.round(((vOrig - vDisc) / vOrig) * 100)
                  : 0;

              return (
                <button
                  key={variantKey}
                  type="button"
                  onClick={() => setSelectedVariantKey(variantKey)}
                  className={`relative flex shrink-0 flex-col items-center rounded-lg px-1.5 pt-2 pb-1 text-center transition-all w-[52px] sm:w-auto sm:min-w-[54px] ${
                    isSelected
                      ? "border-[3px] border-[#00a859] bg-white shadow-sm"
                      : variantInCart
                        ? "border border-emerald-300 bg-emerald-50"
                        : "border border-gray-400 bg-white hover:border-gray-600"
                  }`}
                  aria-pressed={isSelected}
                >
                  {/* Discount % badge */}
                  {vDiscPct > 0 && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-[#f97316] px-1.5 py-0 text-[8px] font-extrabold text-white shadow whitespace-nowrap">
                      {vDiscPct}%
                    </span>
                  )}
                  {/* In-cart qty badge */}
                  {variantInCart && !isSelected && (
                    <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-[7px] font-black text-white shadow">
                      {variantCartItem.quantity}
                    </span>
                  )}
                  <span className="w-full truncate text-[9px] font-bold text-gray-700 leading-tight">
                    {getVariantLabel(variant) || "Variant"}
                  </span>
                  <span className="text-[10px] font-extrabold text-gray-900 leading-tight mt-0.5">
                    ₹{vDisc > 0 ? vDisc.toFixed(0) : "—"}
                  </span>
                  {vOrig > vDisc && (
                    <span className="text-[8px] text-gray-400 line-through leading-none">
                      ₹{vOrig.toFixed(0)}
                    </span>
                  )}
                </button>
              );
            })}

            {variants.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllVariants((prev) => !prev)}
                className="shrink-0 self-center rounded-md border border-dashed border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[10px] font-bold text-gray-500 transition-colors hover:border-[#00a859] hover:text-[#00a859] whitespace-nowrap"
                title={showAllVariants ? "Show fewer" : `${variants.length - 3} more`}
              >
                {showAllVariants ? "Less" : `${variants.length - 3}+ more`}
              </button>
            )}
          </div>
        )}

        {/* ── Pincode checker ── */}
        <div onClick={(e) => e.stopPropagation()} className="mb-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] leading-tight text-gray-600">
              Check Stock Availability Enter Area Pin code
            </span>
            <input
              type="text"
              maxLength={6}
              inputMode="numeric"
              value={pincode}
              onChange={handlePincodeChange}
              onClick={(e) => e.stopPropagation()}
              placeholder={defaultPincode || "Pincode"}
              className={`w-14 rounded border px-1 py-0.5 text-center text-[11px] font-bold outline-none transition-colors shadow-sm ${
                isCheckingPin ? "border-gray-300 text-gray-400 animate-pulse" : pinInputBorder
              }`}
            />
          </div>

          {/* Checking spinner */}
          {isCheckingPin && (
            <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-400">
              <svg className="h-2.5 w-2.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking…
            </div>
          )}

          {/* Result: show pincode-based result OR global OOS message */}
          {((isPincodeComplete && !isCheckingPin) || isProductGloballyOOS) && (
            <div className="mt-1">
              {/* Global OOS from API — show even without pincode */}
              {isProductGloballyOOS && !isPincodeComplete ? (
                <span className="flex items-center gap-1 text-[10px] font-bold">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-amber-500 text-amber-500">
                    <svg className="h-2.5 w-2.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                    </svg>
                  </span>
                  <span className="text-amber-600">Currently Out of Stock</span>
                </span>
              ) : isServiceable && !isOutOfStock ? (
                <span className="flex items-center gap-1 text-[10px] font-bold">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-[#00a859] text-[#00a859]">
                    <svg className="h-2.5 w-2.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {isLowStock && resolvedAvailableStock !== null ? (
                    <>
                      <span className="text-red-600">Only {resolvedAvailableStock} left!</span>
                      <span className="ml-0.5 rounded-full bg-red-100 px-1.5 py-0 text-[9px] font-extrabold text-red-700">LOW STOCK</span>
                    </>
                  ) : isLimitedStock ? (
                    <>
                      <span className="text-[#00a859]">Hurry!</span>
                      <span className="text-[#f97316]">Limited Stock Available</span>
                    </>
                  ) : isInStock ? (
                    <span className="text-[#00a859]">In Stock</span>
                  ) : (
                    <>
                      <span className="text-[#00a859]">Hurry!</span>
                      <span className="text-[#f97316]">Limited Stock Available</span>
                    </>
                  )}
                </span>
              ) : isOutOfStock ? (
                <span className="flex items-center gap-1 text-[10px] font-bold">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-amber-500 text-amber-500">
                    <svg className="h-2.5 w-2.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                    </svg>
                  </span>
                  <span className="text-amber-600">Out of Stock at Your Location</span>
                </span>
              ) : isNotServiceable ? (
                <span className="flex items-center gap-1 text-[10px] font-bold">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-red-500 text-red-500">
                    <FaTimes className="h-2 w-2" />
                  </span>
                  <span className="text-red-500">{pincodeMessage || "Sorry! Delivery Service Not Available in Your Area."}</span>
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* ── Action buttons ── */}
        <div className="mt-auto grid grid-cols-2 gap-1.5">
          {isOutOfStock ? (
            /* ── Out-of-stock: show Notify Me spanning full width ── */
            <>
              {/* "Out of Stock" label row */}
              <div className="col-span-2 flex items-center justify-center gap-1.5 rounded-full bg-gray-100 py-1 text-[11px] font-bold text-gray-500">
                <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Out of Stock
              </div>
              {/* Notify Me button */}
              <CompactNotifyMe
                productId={productId}
                variantId={notifyVariantId}
                pincode={pincode || ""}
                isAuthenticated={isAuthenticated}
                openAuthModal={openAuthModal}
              />
            </>
          ) : cartItem ? (
            /* ── In Cart: qty stepper + Buy Now ── */
            <>
              <div
                className="flex items-center justify-between rounded-full border-2 border-[#00a859] bg-white px-1 py-1 text-[#00a859]"
                onClick={(e) => e.stopPropagation()}
                aria-label={`${product.name} quantity`}
              >
                <button
                  type="button"
                  onClick={handleDecrement}
                  disabled={adding}
                  aria-label={`Decrease ${product.name} quantity`}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm transition hover:bg-emerald-50 disabled:opacity-45"
                >
                  -
                </button>
                <span className="min-w-4 text-center text-xs font-black">{cartItem.quantity}</span>
                <button
                  type="button"
                  onClick={handleIncrement}
                  disabled={adding}
                  aria-label={`Increase ${product.name} quantity`}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00a859] text-sm font-black text-white shadow-sm transition hover:bg-[#00924c] disabled:opacity-45"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleBuyNow}
                disabled={adding}
                className="flex items-center justify-center rounded-full bg-[#00a859] py-1.5 text-xs font-bold text-white shadow-sm transition-all hover:bg-[#00924c] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
              >
                Buy Now
              </button>
            </>
          ) : (
            /* ── Normal: Add to Cart + Buy Now ── */
            <>
              <button
                onClick={handleAddToCart}
                disabled={adding}
                className="flex items-center justify-center gap-1 rounded-full border-2 border-[#00a859] bg-white py-1.5 text-xs font-bold text-[#00a859] transition-all hover:bg-[#00a859] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {adding ? (
                  <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : <FaShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />}
                {adding ? "" : "Cart"}
              </button>
              <button
                onClick={handleBuyNow}
                disabled={adding}
                className="flex items-center justify-center rounded-full bg-[#00a859] py-1.5 text-xs font-bold text-white shadow-sm transition-all hover:bg-[#00924c] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
              >
                Buy Now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductCard);
